<script setup lang="ts">
import { Ref, ref } from 'vue';
import type { BookV1 } from '@ilteducation/content-registry-types';
import Copy from '../Copy.vue';
import useLocale from '../helpers/use-locale';
import type { SharingOptionItems } from '../types/share';

type Emits = {
  (e: 'open-qrcode'): void;
  (e: 'shareByEmail'): void;
  (e: 'copy-url'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  withDefaultOptions: boolean;
  entityUrl?: string;
  shareEntityUrl?: string;
  entityTitle?: string;
  content: 'book' | 'bookshelf' | 'video';
  entity?: BookV1;
  sharingItems?: SharingOptionItems[];
  isAvailableToShare?: boolean;
  rounded?: 'sm' | 'default' | 'lg';
  showTitle?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  showTitle: true,
  rounded: 'lg',
  entityUrl: '',
  shareEntityUrl: '',
  entityTitle: '',
});

const { t } = useLocale();
const defaultIconSize = 32;
const defaultOptionSize = 48;
const defaultOptions: SharingOptionItems[] = [
  {
    key: 'components.google_classroom',
    title: 'components.label_google_classroom',
    bgColor: '#558b2f',
    icon: 'mdi-google-classroom',
    link: 'https://classroom.google.com/share?itemtype=material&url=',
  },
  {
    key: 'components.qr_code',
    title: 'components.label_qr_code',
    bgColor: 'tertiary-fixed-dim',
    icon: 'mdi-qrcode',
    callback: () => {
      emit('open-qrcode');
    },
  },
  {
    key: 'microsoft_teams',
    title: 'components.label_microsoft_teams',
    bgColor: '#4951B9',
    icon: 'mdi-microsoft-teams',
    link: 'https://teams.microsoft.com/share?href=',
  },
  {
    key: 'email',
    title: 'components.label_email',
    bgColor: '#AEE3DA',
    icon: 'mdi-email-outline',
    callback: () => {
      emit('shareByEmail');
    },
  },
];
const sharingOptions: Ref<SharingOptionItems[]> = ref([]);
if (props.withDefaultOptions) {
  sharingOptions.value = [...defaultOptions, ...(props.sharingItems ?? [])];
} else {
  sharingOptions.value = props.sharingItems ?? [];
}
</script>

<template>
  <div>
    <div v-if="showTitle" class="d-flex flex-wrap justify-space-between align-center">
      <p class="mb-1 font-weight-bold">{{ t('components.label_sharing_options') }}</p>
      <slot name="title-right"></slot>
    </div>
    <v-divider v-if="showTitle" :thickness="2" color="outline-variant" class="border-opacity-75 mb-4 mt-1"></v-divider>
    <slot name="subtitle"></slot>
    <div class="d-flex justify-start mb-4">
      <div v-for="option in sharingOptions" :key="option.title" class="text-center mr-md-4">
        <v-btn
          :max-width="option.size ?? defaultOptionSize"
          :min-height="option.size ?? defaultOptionSize"
          :flat="true"
          :disabled="!isAvailableToShare"
          :href="option.link ? `${option.link}${entityUrl}` : undefined"
          :target="option.link ? '_blank' : '_self'"
          variant="plain"
          class="mb-1"
          :aria-label="t(option.title)"
          @click="option.callback ? option.callback() : null"
        >
          <v-avatar
            class="collection-icon text-center"
            :class="rounded === 'default' ? 'rounded' : `rounded-${rounded}`"
            :rounded="0"
            :size="option.size ?? defaultOptionSize"
            :color="option.bgColor"
          >
            <component
              :is="option.customIconComponent"
              v-if="option.customIconComponent"
              :size="option.iconSize ?? defaultIconSize"
              :asset-name="option.icon"
            />
            <v-icon v-else :size="option.size ?? defaultIconSize">{{ option.icon }}</v-icon>
          </v-avatar>
        </v-btn>
        <p class="text-caption">{{ t(option.title) }}</p>
      </div>
    </div>

    <v-card v-if="isAvailableToShare" variant="outlined" :subtitle="t('components.label_copy_url')">
      <v-card-text>
        {{ shareEntityUrl }}
      </v-card-text>

      <template #append>
        <Copy :text-to-copy="shareEntityUrl" @copy="emit('copy-url')" />
      </template>
    </v-card>
  </div>
</template>

<style scoped lang="scss">
.v-btn--variant-plain {
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

.v-btn--disabled {
  opacity: 0.3;
}
</style>
